import { SectionLabel } from "@risalabs_frontend_org/oasis-ui-kit";
import { Fragment } from "react";
import type {
  ManagedCareInformation,
  ThirdPartyPayerInformation,
} from "../../../../data-model/coverageInformation";
import { InfoIcon } from "../../../../svg/info-icon";

/** A single read-only label / value pair. */
interface LabeledField {
  label: string;
  value: string;
}

/** Props for {@link EvCoverageMedicaidPayerSection}. */
interface EvCoverageMedicaidPayerSectionProps {
  title: string;
  fields: LabeledField[];
}

/**
 * Read-only card rendering a titled group of label / value pairs.
 *
 * @param props - Section title and the labeled fields to display
 * @returns A bordered section card with a two-column field grid
 */
const EvCoverageMedicaidPayerSection = ({
  title,
  fields,
}: EvCoverageMedicaidPayerSectionProps) => {
  const rows: LabeledField[][] = [];
  for (let index = 0; index < fields.length; index += 2) {
    rows.push(fields.slice(index, index + 2));
  }

  return (
    <div className="flex flex-col">
      <SectionLabel
        label={title}
        accentColor="#0056D6"
        className="mb-2 !text-tiny !font-bold !leading-tiny !tracking-[0.03125rem] !text-primaryGray-6 sm:mb-3"
      />
      <div className="flex flex-col items-start gap-4 self-stretch rounded-md border border-primaryGray-15 bg-white p-4">
        <div className="flex w-full flex-col items-start gap-4 self-stretch">
          {rows.map((row, rowIndex) => (
            <Fragment key={row.map((field) => field.label).join("-")}>
              {rowIndex > 0 && (
                <div className="w-full self-stretch border-t border-dashed border-primaryGray-14" />
              )}
              <div className="grid w-full grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {row.map((field) => (
                  <div
                    key={field.label}
                    className="flex flex-[1_0_0] flex-col items-start gap-0.5"
                  >
                    <span className="flex w-full flex-col items-start self-stretch text-x-tiny font-bold leading-x-tiny text-primaryGray-6">
                      {field.label}
                    </span>
                    <span className="flex w-full flex-col items-start pt-1 text-tiny font-normal leading-tiny tracking-[0.0125rem] text-primaryGray-1">
                      {field.value}
                    </span>
                  </div>
                ))}
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Determines whether a section has any real data to display.
 *
 * The mapper normalizes missing values to the `"-"` placeholder, so a section
 * is considered empty when every field is blank or `"-"`.
 *
 * @param fields - The labeled fields for a section
 * @returns `true` when at least one field holds a meaningful value
 */
const sectionHasData = (fields: LabeledField[]): boolean =>
  fields.some((field) => {
    const value = field.value.trim();
    return value !== "" && value !== "-";
  });

/** Props for {@link EvCoverageMedicaidPayerPlan}. */
export interface EvCoverageMedicaidPayerPlanProps {
  thirdPartyPayer: ThirdPartyPayerInformation;
  managedCare: ManagedCareInformation;
}

/**
 * Additional Payer / Payer Plan tab for Medicaid coverage.
 *
 * A Medicaid member carries either a Third Party Payer (TPL) block or a Managed
 * Medicaid Care (MMC) block, never both, so only the populated section is
 * rendered. When neither has data, a dedicated empty-state message is shown so
 * the tab is never blank.
 *
 * @param props - Third Party Payer and Managed Care display blocks
 * @returns The Additional Payer / Payer Plan tab body
 */
export const EvCoverageMedicaidPayerPlan = ({
  thirdPartyPayer,
  managedCare,
}: EvCoverageMedicaidPayerPlanProps) => {
  const thirdPartyPayerFields: LabeledField[] = [
    { label: "Payer Name", value: thirdPartyPayer.payerName },
    { label: "Carrier Code", value: thirdPartyPayer.carrierCode },
    { label: "Address", value: thirdPartyPayer.address },
    { label: "Phone", value: thirdPartyPayer.phone },
    { label: "Policy Number", value: thirdPartyPayer.policyNumber },
    { label: "Group Number", value: thirdPartyPayer.groupNumber },
  ];
  const managedCareFields: LabeledField[] = [
    { label: "Plan Name", value: managedCare.planName },
    { label: "Plan Code", value: managedCare.planCode },
    { label: "Phone", value: managedCare.phone },
    { label: "Address", value: managedCare.address },
  ];

  const showThirdPartyPayer = sectionHasData(thirdPartyPayerFields);
  const showManagedCare = sectionHasData(managedCareFields);
  const showFallback = !showThirdPartyPayer && !showManagedCare;

  return (
    <div className="flex min-h-full flex-col gap-4 md:gap-6">
      {showThirdPartyPayer && (
        <EvCoverageMedicaidPayerSection
          title="Third Party Payer"
          fields={thirdPartyPayerFields}
        />
      )}
      {showManagedCare && (
        <EvCoverageMedicaidPayerSection
          title="MMC"
          fields={managedCareFields}
        />
      )}
      {showFallback && (
        <div className="flex flex-1 flex-col items-center justify-center overflow-auto rounded-lg border border-primaryGray-15 bg-white p-4">
          <div className="flex w-full max-w-4xl flex-col items-center justify-center gap-3 px-4 py-6">
            <InfoIcon
              height="28"
              width="28"
              stroke="#0056D6"
              strokeWidth="1.5"
            />
            <p className="text-center text-small font-normal text-tertiaryBlue-2">
              This member does not have a third-party payer or managed care plan
              on file for this coverage.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvCoverageMedicaidPayerPlan;
